import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { Landmark, Globe, Search, AlertTriangle, RotateCw, ChevronRight, Building2, Users, Network } from "lucide-react";
import { api } from "../lib/api";
import { Flag } from "../components/Flag";
import { StatusBadge } from "../components/Badges";

const CONTAINER = "mx-auto max-w-[1700px] px-4 sm:px-8 lg:px-12";
const LEVEL_LABEL = { WORLD: "World Body", CONTINENTAL: "Continental Confederation", NATIONAL: "National Federation" };

function SummaryStat({ label, value, Icon }) {
  return (
    <div className="inline-flex items-center gap-2 text-sm font-mono">
      <Icon className="w-4 h-4 text-[#E5B842]" />
      <span className="font-heading text-xl font-black text-[#E5B842] tabular-nums">{value}</span>
      <span className="uppercase tracking-widest text-slate-500 text-xs">{label}</span>
    </div>
  );
}

function TierLabel({ index, label, count }) {
  return (
    <div className="flex items-center justify-center gap-3 mb-5">
      <span className="font-mono text-[11px] font-bold tracking-[0.35em] text-[#E5B842]">TIER {index}</span>
      <span className="h-px w-8 bg-[#E5B842]/40" />
      <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-slate-400">{label}</span>
      {count != null && <span className="font-mono text-[11px] text-slate-600">· {count}</span>}
    </div>
  );
}

function Connector({ label }) {
  return (
    <div className="flex flex-col items-center py-3 select-none" aria-hidden="true">
      <span className="w-px h-6 bg-gradient-to-b from-[#E5B842]/60 to-[#E5B842]/10" />
      {label && (
        <span className="my-1 font-mono text-[9px] uppercase tracking-[0.3em] text-[#E5B842]/70 inline-flex items-center gap-1">
          <Network className="w-3 h-3" />{label}
        </span>
      )}
      <span className="w-px h-6 bg-gradient-to-b from-[#E5B842]/10 to-[#E5B842]/60" />
    </div>
  );
}

function ApexCard({ f, childCount }) {
  return (
    <Link
      to={`/federation/${f.wsbf_fed_id}`}
      data-testid={`federation-card-${f.wsbf_fed_id}`}
      className="group relative block mx-auto max-w-[680px] rounded-2xl foil-border bg-gradient-to-b from-[#1a1407] to-[#070707] overflow-hidden"
      style={{ transition: "transform 0.2s ease, box-shadow 0.2s ease" }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 18px 50px -14px rgba(212,175,55,0.5)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = ""; }}
    >
      <div className="absolute inset-0 mesh-grid opacity-[0.2] pointer-events-none" />
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(100% 140% at 50% 0%, rgba(212,175,55,0.2) 0%, transparent 60%)" }} />
      <div className="relative p-7 sm:p-9 flex flex-col sm:flex-row items-center text-center sm:text-left gap-6">
        <div className="relative shrink-0 w-24 h-24 rounded-2xl bg-[#000000] border border-[#E5B842]/50 flex items-center justify-center">
          <Globe className="w-12 h-12 text-[#E5B842]" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-center sm:justify-start gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-[0.28em] text-[#E5B842] font-mono font-bold">{LEVEL_LABEL[f.level]}</span>
            <StatusBadge status={f.recognition_status} />
          </div>
          <div className="font-heading text-2xl sm:text-4xl font-black uppercase tracking-tight text-slate-50 leading-none mt-1.5 wsbf-slant-block">{f.name}</div>
          <div className="mt-3 flex items-center justify-center sm:justify-start gap-x-6 gap-y-1 flex-wrap font-mono text-[11px]">
            <span className="text-[#E5B842] tracking-widest">{f.wsbf_fed_id}</span>
            <span className="inline-flex items-center gap-1 text-slate-400"><Landmark className="w-3 h-3" />{childCount} confederation{childCount === 1 ? "" : "s"}</span>
          </div>
        </div>
        <ChevronRight className="hidden sm:block w-6 h-6 text-slate-600 group-hover:text-[#E5B842]" style={{ transition: "color 0.18s ease" }} />
      </div>
    </Link>
  );
}

function ContinentalCard({ f, childCount }) {
  return (
    <Link
      to={`/federation/${f.wsbf_fed_id}`}
      data-testid={`federation-card-${f.wsbf_fed_id}`}
      className="group relative block mx-auto max-w-[860px] rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.3)] overflow-hidden"
      style={{ transition: "border-color 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease" }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 12px 34px -14px rgba(212,175,55,0.4)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = ""; }}
    >
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(90% 120% at 85% 0%, rgba(212,175,55,0.1) 0%, transparent 55%)" }} />
      <div className="relative p-5 sm:p-6 flex items-center gap-5">
        <div className="relative shrink-0 w-16 h-16 rounded-xl bg-[#000000] border border-[#E5B842]/40 flex items-center justify-center">
          <Landmark className="w-8 h-8 text-[#E5B842]" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-mono">{LEVEL_LABEL[f.level]}</span>
            <StatusBadge status={f.recognition_status} />
          </div>
          <div className="font-heading text-xl sm:text-2xl font-black uppercase tracking-tight text-slate-50 truncate leading-tight">{f.name}</div>
          <div className="mt-1 flex items-center gap-x-5 flex-wrap font-mono text-[11px]">
            <span className="text-[#E5B842] tracking-widest">{f.wsbf_fed_id}</span>
            <span className="inline-flex items-center gap-1 text-slate-400"><Landmark className="w-3 h-3" />{childCount} national federation{childCount === 1 ? "" : "s"}</span>
          </div>
        </div>
        <ChevronRight className="w-6 h-6 text-slate-600 group-hover:text-[#E5B842]" style={{ transition: "color 0.18s ease" }} />
      </div>
    </Link>
  );
}

// National-federation emblem = real local country flag (/public/assets/flags/{iso3}.png).
// To add a new country: drop {iso3-lowercase}.png into that folder — no code change needed.
// Falls back to the country code initials if the flag file is missing (never a broken image).
function FedEmblem({ f }) {
  const iso3 = f.country?.iso3 ? f.country.iso3.toLowerCase() : null;
  const [imgError, setImgError] = useState(false);
  const showFlag = iso3 && !imgError;
  return (
    <div className="relative shrink-0 w-[52px] h-[52px] rounded-xl bg-[#000000] border border-[#E5B842]/30 overflow-hidden flex items-center justify-center">
      {showFlag ? (
        <img
          src={`/assets/flags/${iso3}.png`}
          alt={`${f.country.name} flag`}
          loading="lazy"
          onError={() => setImgError(true)}
          className="w-full h-full object-cover"
        />
      ) : f.country ? (
        <span className="font-mono text-[11px] font-bold text-[#E5B842] tracking-wide">{f.country.sporty || f.country.iso3}</span>
      ) : (
        <Landmark className="w-[26px] h-[26px] text-[#E5B842]" />
      )}
    </div>
  );
}

function FedCard({ f }) {
  // Long names get a slightly smaller type size so the FULL name always fits (never truncated).
  const nameLen = (f.name || "").length;
  const nameSize = nameLen > 34 ? "text-sm" : nameLen > 24 ? "text-[15px]" : "text-base";
  return (
    <Link
      to={`/federation/${f.wsbf_fed_id}`}
      data-testid={`federation-card-${f.wsbf_fed_id}`}
      className="group relative h-full flex flex-col rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.16)] p-5 hover:border-[#E5B842]/55"
      style={{ transition: "border-color 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease" }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 10px 30px -12px rgba(212,175,55,0.35)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = ""; }}
    >
      <div className="flex items-start gap-3">
        <FedEmblem f={f} />
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono">{LEVEL_LABEL[f.level]}</div>
          <div className={`font-heading ${nameSize} font-bold uppercase tracking-tight text-slate-50 leading-snug`}>{f.name}</div>
          {f.country && (
            <div className="text-xs text-slate-400 mt-1 inline-flex items-center gap-1.5">
              <span className="leading-none" aria-hidden="true"><Flag country={f.country} width={16} /></span>
              <span className="truncate">{f.country.name}</span>
            </div>
          )}
        </div>
        <StatusBadge status={f.recognition_status} />
      </div>
      <div className="flex items-center justify-between mt-auto pt-4">
        <span className="font-mono text-[11px] text-[#E5B842] tracking-widest">{f.wsbf_fed_id}</span>
        <span className="flex items-center gap-3 font-mono text-[11px] text-slate-400">
          <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3" />{f.clubs_count}</span>
          <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" />{f.players_count}</span>
          <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-[#E5B842]" style={{ transition: "color 0.18s ease" }} />
        </span>
      </div>
    </Link>
  );
}

function SkeletonCard() {
  return (
    <div className="rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.14)] p-5 animate-pulse">
      <div className="flex items-center gap-3"><div className="w-12 h-12 rounded-lg bg-[#1a1407]" /><div className="flex-1 space-y-2"><div className="h-4 w-2/3 bg-[#1a1407] rounded" /><div className="h-3 w-1/3 bg-[#141414] rounded" /></div></div>
    </div>
  );
}

export default function FederationsList() {
  const [feds, setFeds] = useState(null);
  const [error, setError] = useState(false);
  const [q, setQ] = useState("");

  const load = useCallback(() => {
    setFeds(null); setError(false);
    api.get("/federations").then(({ data }) => setFeds(data.federations)).catch(() => setError(true));
  }, []);
  useEffect(() => { load(); }, [load]);

  const { world, continentals, nationalsByParent, childCount } = useMemo(() => {
    const list = feds || [];
    const world = list.filter((f) => f.level === "WORLD");
    const continentals = list.filter((f) => f.level === "CONTINENTAL");
    const nationals = list.filter((f) => f.level === "NATIONAL");
    const nationalsByParent = {};
    nationals.forEach((n) => {
      const key = n.parent_id || "_";
      (nationalsByParent[key] = nationalsByParent[key] || []).push(n);
    });
    const childCount = {};
    list.forEach((p) => { childCount[p.id] = list.filter((c) => c.parent_id === p.id).length; });
    return { world, continentals, nationalsByParent, childCount };
  }, [feds]);

  const filtered = (feds || []).filter(
    (f) => !q || f.name.toLowerCase().includes(q.toLowerCase()) || f.wsbf_fed_id.toLowerCase().includes(q.toLowerCase()) || (f.country?.name || "").toLowerCase().includes(q.toLowerCase())
  );
  const total = feds?.length || 0;
  const active = (feds || []).filter((f) => f.recognition_status === "ACTIVE").length;
  const countries = new Set((feds || []).filter((f) => f.country).map((f) => f.country.iso3)).size;
  const searching = q.trim().length > 0;

  return (
    <div className="animate-fade-up pb-20">
      <section className="relative overflow-hidden border-b border-[rgba(212,175,55,0.22)] bg-[#000000]">
        <div className="absolute inset-0 mesh-grid opacity-[0.18] pointer-events-none" />
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(120% 140% at 80% 15%, rgba(212,175,55,0.14) 0%, transparent 58%)" }} />
        <div className={`${CONTAINER} relative py-12 sm:py-14`}>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#E5B842] font-mono font-bold mb-3">WSTF Governance</div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-slate-50 leading-none wsbf-slant-block break-words">The Governance Pyramid</h1>
          <p className="text-slate-400 mt-3 text-sm sm:text-base max-w-2xl">A top-down institutional hierarchy — world body, continental confederations, and the national federations that answer to them.</p>
          {feds && (
            <div className="mt-5 flex flex-wrap items-center gap-x-8 gap-y-3" data-testid="federations-summary">
              <SummaryStat label="Federations" value={total} Icon={Landmark} />
              <SummaryStat label="Active" value={active} Icon={Landmark} />
              <SummaryStat label="Countries" value={countries} Icon={Globe} />
            </div>
          )}
        </div>
      </section>

      <div className={`${CONTAINER} pt-8`}>
        <div className="relative max-w-3xl">
          <Search className="w-5 h-5 text-[#E5B842]/70 absolute left-4 top-1/2 -translate-y-1/2" />
          <input
            data-testid="federations-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by federation name, country, or WSTF Fed ID"
            className="w-full bg-[#0a0a0a] border border-[rgba(212,175,55,0.22)] rounded-xl pl-12 pr-4 py-4 text-slate-100 text-base placeholder:text-slate-600 focus:outline-none focus:border-[#E5B842]/70 focus:ring-2 focus:ring-[#E5B842]/25"
            style={{ transition: "border-color 0.15s ease, box-shadow 0.15s ease" }}
          />
        </div>
      </div>

      <div className={`${CONTAINER} pt-10`}>
        {error ? (
          <div data-testid="federations-error" className="text-center py-20 border border-dashed border-rose-500/30 rounded-xl">
            <AlertTriangle className="w-7 h-7 text-rose-400 mx-auto mb-3" />
            <div className="text-slate-300 font-mono text-sm uppercase tracking-widest mb-4">Could not load federations</div>
            <button onClick={load} data-testid="federations-retry" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold uppercase tracking-wide bg-[#161616] border border-[rgba(212,175,55,0.3)] text-slate-200 hover:border-[#E5B842]/60 hover:text-[#E5B842]" style={{ transition: "border-color 0.15s ease, color 0.15s ease" }}>
              <RotateCw className="w-4 h-4" /> Retry
            </button>
          </div>
        ) : !feds ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="federations-skeleton">
            {Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : searching ? (
          filtered.length === 0 ? (
            <div data-testid="federations-empty" className="text-center py-20 text-slate-500 font-mono text-sm uppercase tracking-widest border border-dashed border-[rgba(212,175,55,0.25)] rounded-xl">
              No federations match “{q}”
            </div>
          ) : (
            <>
              <TierLabel index="—" label={`Search results`} count={filtered.length} />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="federations-list">
                {filtered.map((f) => <FedCard key={f.id} f={f} />)}
              </div>
            </>
          )
        ) : (
          <div data-testid="federations-pyramid">
            {/* TIER 1 — World body */}
            {world.map((w) => (
              <div key={w.id}>
                <TierLabel index="01" label="World Body" />
                <ApexCard f={w} childCount={childCount[w.id] || 0} />
                <Connector label="governs" />
              </div>
            ))}

            {/* TIER 2 — Continental confederations + their national members */}
            <TierLabel index="02" label="Continental Confederations" count={continentals.length} />
            <div className="space-y-12">
              {continentals.map((c) => {
                const kids = nationalsByParent[c.id] || [];
                return (
                  <div key={c.id}>
                    <ContinentalCard f={c} childCount={kids.length} />
                    {kids.length > 0 && (
                      <>
                        <Connector label="member federations" />
                        <TierLabel index="03" label={`National Federations · ${c.short_code || c.name}`} count={kids.length} />
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                          {kids.map((n) => <FedCard key={n.id} f={n} />)}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Orphan nationals (defensive — none expected) */}
            {(() => {
              const contIds = new Set(continentals.map((c) => c.id));
              const orphans = Object.entries(nationalsByParent)
                .filter(([pid]) => pid === "_" || !contIds.has(pid))
                .flatMap(([, arr]) => arr);
              if (!orphans.length) return null;
              return (
                <div className="mt-12">
                  <TierLabel index="03" label="Unaffiliated National Federations" count={orphans.length} />
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {orphans.map((n) => <FedCard key={n.id} f={n} />)}
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
}
